// API client pour la gestion des produits
import { safeFetch, safeJsonParse, getAuthHeaders } from './api';
import type { Product, ProductCategory, TaxRate, PriceTier, PricingMode, ProductClientPriceEntry, ClientPriceEntry } from '../types/models';
import { logger } from './logger';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

// ============================================
// PRODUCT API TYPES
// ============================================

/**
 * Interface pour créer un produit (Prisma V2 conforme)
 */
export interface ProductCreate {
  name: string;
  description?: string;
  technicalDescription?: string;
  sku?: string;
  unit?: string;
  unitPrice?: number;
  currencyCode?: string;
  categoryId?: string;
  taxRateId?: string;
  trackInventory?: boolean;
  stockQuantity?: number | null;
  lowStockAlert?: number | null;
  // S7 — Nature comptable et ouvrages composés
  productType?: 'FOURNITURE' | 'MAIN_OEUVRE' | 'SOUS_TRAITANCE' | 'MATERIEL' | 'SERVICE' | 'DIVERS';
  costPrice?: number | null;
  marginPercent?: number | null;
  isComposite?: boolean;
}

export interface ProductUpdate extends Partial<ProductCreate> {}

/** S7 — DTO pour ajouter un composant */
export interface AddComponentInput {
  childProductId: string;
  quantity: number;
  unit?: string;
  costPriceOverride?: number | null;
  marginPercentOverride?: number | null;
}

export interface ProductSalesStat {
  id: string;  // UUID
  name: string;
  sku?: string;
  unitPrice: number;
  timesQuoted: number;
  timesInvoiced: number;
}


/**
 * API Catégories (V2 - uses ProductCategory)
 */
export const categoriesAPI = {
  /**
   * Récupérer toutes les catégories
   */
  async getAll(): Promise<ProductCategory[]> {
    const response = await safeFetch(`${API_URL}/catalog/categories`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la récupération des catégories');
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Créer une nouvelle catégorie
   */
  async create(categoryData: { name: string; description?: string }): Promise<ProductCategory> {
    const response = await safeFetch(`${API_URL}/catalog/categories`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(categoryData),
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la création de la catégorie');
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Mettre à jour une catégorie
   */
  async update(categoryId: string, categoryData: { name?: string; description?: string }): Promise<ProductCategory> {
    const response = await safeFetch(
      `${API_URL}/catalog/categories/${categoryId}`,
      {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(categoryData),
      }
    );

    if (!response.ok) {
      throw new Error('Erreur lors de la mise à jour de la catégorie');
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Supprimer une catégorie
   */
  async delete(categoryId: string): Promise<void> {
    const response = await safeFetch(
      `${API_URL}/catalog/categories/${categoryId}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error('Erreur lors de la suppression de la catégorie');
    }
  },
};

/**
 * API Produits
 */
export const productsAPI = {
  // Récupérer tous les produits
  getAll: async (params?: {
    categoryId?: string;
    active?: boolean;
    search?: string;
    limit?: number;
  }): Promise<{ success?: boolean; data?: Product[]; products?: Product[]; meta?: { total: number; totalPages: number; currentPage: number } }> => {
    const queryParams = new URLSearchParams();
    if (params?.categoryId) queryParams.append("categoryId", params.categoryId);
    if (params?.active !== undefined)
      queryParams.append("active", String(params.active));
    if (params?.search) queryParams.append("search", params.search);
    if (params?.limit) queryParams.append("limit", String(params.limit));

    const url = `${API_URL}/catalog/products${
      queryParams.toString() ? `?${queryParams.toString()}` : ""
    }`;
    const response = await safeFetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des produits");
    }

    const data = await safeJsonParse(response);
    return data;
  },

  // Récupérer un produit spécifique
  getById: async (id: string): Promise<Product> => {  // ✅ CORRIGÉ: UUID string
    const response = await safeFetch(`${API_URL}/catalog/products/${id}`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération du produit");
    }

    const apiProduct = await safeJsonParse(response);
    return apiProduct.data || apiProduct;
  },

  // Créer un nouveau produit
  create: async (product: ProductCreate): Promise<Product> => {
    const response = await safeFetch(`${API_URL}/catalog/products`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(product),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || "Erreur lors de la création du produit");
    }

    const apiProduct = await safeJsonParse(response);
    return apiProduct.data || apiProduct;
  },

  // Mettre à jour un produit
  update: async (id: string, product: ProductUpdate): Promise<Product> => {  // ✅ CORRIGÉ: UUID string
    const response = await safeFetch(`${API_URL}/catalog/products/${id}`, {
      method: "PATCH",  // ✅ Backend utilise PATCH, pas PUT
      headers: getAuthHeaders(),
      body: JSON.stringify(product),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(
        error.error || "Erreur lors de la mise à jour du produit"
      );
    }

    const apiProduct = await safeJsonParse(response);
    return apiProduct.data || apiProduct;
  },

  // Supprimer un produit
  delete: async (id: string): Promise<void> => {  // ✅ CORRIGÉ: UUID string
    const response = await safeFetch(`${API_URL}/catalog/products/${id}`, {
      method: "DELETE",
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(
        error.error || "Erreur lors de la suppression du produit"
      );
    }
  },

  // Récupérer les taux de TVA disponibles
  // ✅ CORRIGÉ: Tax-rates déplacé vers /tax/tax-rates (Sprint 6)
  getTVARates: async (): Promise<TaxRate[]> => {
    const response = await safeFetch(`${API_URL}/tax/tax-rates`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      // Si l'API échoue, lancer une erreur plutôt que retourner des données incorrectes
      logger.error('Failed to fetch tax rates from API');
      throw new Error('Impossible de récupérer les taux de TVA');
    }

    const data = await safeJsonParse(response);
    if (Array.isArray(data)) return data as TaxRate[];
    if (Array.isArray(data?.data)) return data.data as TaxRate[];
    if (Array.isArray(data?.taxRates)) return data.taxRates as TaxRate[];
    return [] as TaxRate[];
  },

  // Récupérer les statistiques des produits
  getStats: async (): Promise<any> => {
    const response = await safeFetch(`${API_URL}/catalog/products/stats`, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des statistiques");
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  // Alias pour compatibilité
  getSalesStats: async (): Promise<ProductSalesStat[]> => {
    const stats = await productsAPI.getStats();
    return stats.salesStats || [];
  },

  // ============================================
  // STOCK MANAGEMENT API
  // ============================================

  /**
   * Ajuster le stock d'un produit
   */
  adjustStock: async (
    productId: string,
    data: {
      type: 'IN' | 'OUT' | 'ADJUSTMENT' | 'INITIAL' | 'RETURN' | 'TRANSFER';
      quantity: number;
      reason?: string;
      referenceType?: string;
      referenceId?: string;
    }
  ): Promise<{ product: Product; stockMovement: any }> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/adjust-stock`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de l'ajustement du stock");
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Récupérer les produits avec stock bas
   */
  getLowStock: async (limit?: number): Promise<Product[]> => {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', String(limit));

    const url = `${API_URL}/catalog/products/low-stock${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await safeFetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des produits en stock bas");
    }

    const result = await safeJsonParse(response);
    return result.data || result;
  },

  // ============================================
  // S7 — COMPOSITE PRODUCT API (Ouvrages Composés)
  // ============================================

  /**
   * Récupérer un produit avec ses composants
   */
  getProductWithComponents: async (productId: string): Promise<any> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/components`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des composants");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Ajouter un composant à un ouvrage composé
   */
  addComponent: async (productId: string, data: AddComponentInput): Promise<any> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/components`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de l'ajout du composant");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Mettre à jour un composant
   */
  updateComponent: async (productId: string, componentId: string, data: Partial<AddComponentInput>): Promise<any> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/components/${componentId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de la mise à jour du composant");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Supprimer un composant
   */
  removeComponent: async (productId: string, componentId: string): Promise<void> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/components/${componentId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de la suppression du composant");
    }
  },

  /**
   * Réordonner les composants
   */
  reorderComponents: async (productId: string, componentIds: string[]): Promise<void> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/components/reorder`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({ componentIds }),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors du réordonnement");
    }
  },

  /**
   * Exploser un ouvrage composé en lignes de document (pour devis/facture)
   */
  explodeToLines: async (productId: string): Promise<any[]> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/explode`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de l'explosion de l'ouvrage");
    }
    const result = await safeJsonParse(response);
    return result.data || [];
  },

  // ============================================
  // S10 — TIERED PRICING API (Tarifs Dégressifs)
  // ============================================

  /**
   * Récupérer les paliers de prix d'un produit
   * Retourne également le prix unitaire de base du produit (pour disambiguation)
   */
  getTiers: async (productId: string): Promise<{ pricingMode: PricingMode; tiers: PriceTier[]; baseUnitPrice: number }> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/tiers`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des paliers de prix");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Définir les paliers de prix d'un produit
   */
  setTiers: async (
    productId: string,
    data: { pricingMode: PricingMode; tiers: Array<{ minQuantity: number; unitPrice: number }> }
  ): Promise<{ pricingMode: PricingMode; tiers: PriceTier[] }> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/tiers`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de la mise à jour des paliers");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Résoudre le prix effectif d'un produit pour une quantité
   */
  resolvePrice: async (
    productId: string,
    quantity: number
  ): Promise<{
    quantity: number;
    baseUnitPrice: number;
    effectiveUnitPrice: number;
    tierApplied: boolean;
    tierDetail?: string;
    pricingMode: PricingMode;
  }> => {
    const response = await safeFetch(
      `${API_URL}/catalog/products/${productId}/resolve-price?quantity=${quantity}`,
      { headers: getAuthHeaders() }
    );
    if (!response.ok) {
      throw new Error("Erreur lors de la résolution du prix");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Récupérer l'historique des mouvements de stock d'un produit
   */
  getStockMovements: async (
    productId: string,
    params?: { page?: number; limit?: number }
  ): Promise<{
    movements: any[];
    total: number;
    totalPages: number;
    currentPage: number;
  }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));

    const url = `${API_URL}/catalog/products/${productId}/stock-movements${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await safeFetch(url, {
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      throw new Error("Erreur lors de la récupération de l'historique du stock");
    }

    const result = await safeJsonParse(response);
    return {
      movements: result.data || [],
      total: result.meta?.total || 0,
      totalPages: result.meta?.totalPages || 1,
      currentPage: result.meta?.currentPage || 1,
    };
  },

  // ============================================
  // S13 — CLIENT PRICING API (Tarifs Spécifiques par Client)
  // ============================================

  /**
   * Récupérer les tarifs clients d'un produit
   */
  getProductClientPrices: async (productId: string): Promise<ProductClientPriceEntry[]> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/client-prices`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la récupération des tarifs clients");
    }
    const result = await safeJsonParse(response);
    return result.data || [];
  },

  /**
   * Définir un tarif client pour un produit
   */
  setProductClientPrice: async (
    productId: string,
    clientId: string,
    data: { customPrice: number; notes?: string; validFrom?: string; validUntil?: string }
  ): Promise<any> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/client-prices/${clientId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de la définition du tarif client");
    }
    const result = await safeJsonParse(response);
    return result.data || result;
  },

  /**
   * Supprimer un tarif client pour un produit
   */
  removeProductClientPrice: async (productId: string, clientId: string): Promise<void> => {
    const response = await safeFetch(`${API_URL}/catalog/products/${productId}/client-prices/${clientId}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error.error || error.message || "Erreur lors de la suppression du tarif client");
    }
  },

  /**
   * Résoudre les prix négociés d'un client pour un lot de produits
   */
  resolveClientPrices: async (
    clientId: string,
    productIds: string[]
  ): Promise<Record<string, number>> => {
    const response = await safeFetch(`${API_URL}/catalog/clients/${clientId}/resolve-prices`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({ productIds }),
    });
    if (!response.ok) {
      throw new Error("Erreur lors de la résolution des prix clients");
    }
    const result = await safeJsonParse(response);
    return result.data || {};
  },
};


